import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { IChingCoinFace } from '../../core/iching.models';
import { injectIChingTranslate } from '../iching-consult-translate';

/**
 * Drie munten van de laatste worp (kop = 3, munt = 2) met hun som. Zonder
 * worp tonen drie lege munten de plek waar ze straks vallen.
 */
@Component({
  selector: 'hd-iching-coin-throw',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .coin {
      display: inline-flex; align-items: center; justify-content: center;
      width: 44px; height: 44px; border-radius: 50%;
      border: 2px solid var(--hd-iching-coin-border, rgba(0, 0, 0, 0.25));
      background: var(--hd-iching-coin-bg, rgba(212, 168, 105, 0.12));
      font-weight: 600; font-size: 16px; color: var(--text-primary, inherit);
    }
    .coin.is-head { background: var(--hd-iching-coin-head-bg, rgba(212, 168, 105, 0.35)); }
    .coin.is-empty { border-style: dashed; background: transparent; }
    .sum { margin-left: 4px; font-size: 13px; opacity: 0.85; }
    .sum strong { font-size: 18px; }
  `,
  template: `
    @for (coin of coins(); track $index) {
      <span class="coin" [class.is-head]="coin === 'head'" [class.is-empty]="coin === undefined"
            [attr.data-face]="coin" [attr.title]="coin ? t('humanDesign.iching.cast.' + coin) : undefined">
        {{ coin === undefined ? '' : letter(coin) }}
      </span>
    }
    @if (sum(); as total) {
      <span class="sum" data-test-id="iching-coin-sum">{{ t('humanDesign.iching.cast.sum') }}: <strong>{{ total }}</strong></span>
    }
  `,
})
export class HdIChingCoinThrowComponent {
  readonly faces = input<readonly IChingCoinFace[] | undefined>(undefined);

  protected readonly t = injectIChingTranslate();

  protected readonly coins = computed<(IChingCoinFace | undefined)[]>(() => {
    const faces = this.faces();
    return [0, 1, 2].map(i => faces?.[i]);
  });

  protected readonly sum = computed(() => {
    const faces = this.faces();
    return faces?.length === 3 ? faces.reduce((total, face) => total + (face === 'head' ? 3 : 2), 0) : undefined;
  });

  protected letter(face: IChingCoinFace): string {
    return this.t(`humanDesign.iching.cast.${face}`).charAt(0).toUpperCase();
  }
}
